import type { User } from '@access-evo/react-app-api';
import { EvoMfesProvider } from '@access-evo/react-app-api';
import {
  type ProductSettings,
  useProductSettings,
} from '@workspace/utils-react';
import { ReactNode, useRef } from 'react';
import useIdentity from '../useIdentity';

interface EvoMfesProviderWrapperProps {
  children: ReactNode;
  instanceId?: string;
}

interface SelfQueryResponse {
  data: {
    self: {
      items: Array<{
        id: string;
        emailAddress: string;
        firstName: string;
        lastName: string;
        organisationUser: {
          organisation: {
            id: string;
            name: string;
          };
        };
      }>;
    };
  };
}

const SELF_QUERY = `{
  self {
    items {
      emailAddress
      id
      firstName
      lastName
      organisationUser {
        organisation {
          id
          name
        }
      }
    }
  }
}`;

const isLocalDevelopment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

export function EvoMfesProviderWrapper({
  children,
  instanceId,
}: EvoMfesProviderWrapperProps): React.ReactElement {
  const { user: oidcUser, getAccessToken } = useIdentity();
  const userCacheRef = useRef<User | null>(null);
  const settings = useProductSettings<ProductSettings>();

  const getGraphQLUrl = (): string => {
    if (isLocalDevelopment()) {
      return '/api/platform-graphql';
    }
    return settings?.platformGraphqlUrl || '';
  };

  const getEvoUser = async (): Promise<User> => {
    if (!oidcUser) {
      throw new Error('User not authenticated');
    }

    // Return cached user if available
    if (userCacheRef.current) {
      return userCacheRef.current;
    }

    // Use refresh-aware getAccessToken instead of oidcUser.access_token
    const accessToken = await getAccessToken();

    const response = await fetch(getGraphQLUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ query: SELF_QUERY }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user data: ${response.status}`);
    }

    const result: SelfQueryResponse = await response.json();
    const selfUser = result.data?.self?.items?.[0];

    if (!selfUser) {
      throw new Error('No user data returned from GraphQL API');
    }

    // Map GraphQL response to User type expected by @access-evo/react-app-api
    const user: User = {
      id: selfUser.id,
      emailAddress: selfUser.emailAddress,
      firstName: selfUser.firstName,
      lastName: selfUser.lastName,
      organisation: {
        id: selfUser.organisationUser?.organisation?.id || '',
        name: selfUser.organisationUser?.organisation?.name || '',
      },
    };

    // Cache the user data
    userCacheRef.current = user;

    return user;
  };

  return (
    <EvoMfesProvider
      hostName="cash-forecaster"
      environment="preprod"
      geography="uk"
      getEvoUser={getEvoUser}
      getAccessToken={getAccessToken}
      addImportMap={false}
    >
      {children}
    </EvoMfesProvider>
  );
}
